import { useCallback, useMemo } from "react";

import { useAppSelector } from "@/redux/hooks";

import { useGetScoreCriteriaQuery, useGetScoreQuery } from "@/pages/Private/redux/score/score.api";
import { criteriaSelector, scoreSelector } from "@/pages/Private/redux/score/score.slice";

import { ScoreCriteriaType, ScoreResponse } from "../schema/score.schema";

import { computeScoreIssues, ScoreIssue } from "./scoreIssues";

export interface ScoreConfig {
	score: ScoreResponse | null | undefined;
	criteria: ScoreCriteriaType[];
	isLoading: boolean;
	/** Re-reads both the score and its lead scorings; resolves once the slice holds the fresh data. */
	refetch: () => Promise<unknown>;
}

/**
 * The one place a settings page reads the score and its lead scorings from
 * (PREC-4055). Both queries are mirrored into the score slice, so every page
 * and the tab strip see the same object — the pages used to keep three copies
 * (local state, the slice, and direct query data) that could disagree.
 */
export const useScoreConfig = (): ScoreConfig => {
	const { isLoading: scoreLoading, refetch: refetchScore } = useGetScoreQuery();
	const { isLoading: criteriaLoading, refetch: refetchCriteria } = useGetScoreCriteriaQuery();

	const { score } = useAppSelector(scoreSelector);
	const criteria = useAppSelector(criteriaSelector);

	const refetch = useCallback(
		() => Promise.all([refetchScore(), refetchCriteria()]),
		[refetchScore, refetchCriteria]
	);

	return { score, criteria, isLoading: scoreLoading || criteriaLoading, refetch };
};

/** The configuration's issues, recomputed only when the configuration changes. */
export const useScoreIssues = (): ScoreIssue[] => {
	const { score, criteria } = useScoreConfig();

	return useMemo(() => computeScoreIssues({ score, criteria }), [score, criteria]);
};
